import { ChildrenProps, useState } from 'react';

import { AppStatus, loaders } from 'entities';

import { Column, Row, Loader, Button } from 'components/Blocks';
import { RefreshIcon, WarningIcon } from 'components/Blocks/Icons';

import { useAuth } from 'providers/Auth';

import { typedStorage } from 'utils/storage';
import { env } from 'config/env';
import { useLang } from 'locale';

type AppBlock = 'VERSION' | 'MAINTENANCE' | null;

const { appBlock } = typedStorage<{ appBlock: AppBlock }>().init({
  appBlock: 'app_block',
});

export function SecurityGuard({ children }: ChildrenProps) {
  const { t } = useLang();
  const { isLogged, logout } = useAuth();

  const [block, setBlock] = useState<AppBlock>(() => appBlock.get() ?? null);

  const reload = () => location.reload();

  function handleBlockUpdate({ logout: shouldLogout, maintenance, version }: AppStatus): void {
    const newBlock: AppBlock = maintenance
      ? 'MAINTENANCE'
      : version.enforceUpdate
        ? 'VERSION'
        : null;

    setBlock(newBlock);

    newBlock ? appBlock.set(newBlock) : appBlock.delete();

    if (shouldLogout && isLogged) logout();

    // Let the api ensure its all good to delete it from LS
    if (!block && newBlock === 'VERSION') setTimeout(reload, 2_000);
  }

  const { retry, loading } = loaders.infra.appStatus.use({
    defaultValue: {
      maintenance: false,
      logout: false,
      version: { enforceUpdate: false, logout: false },
    },

    params: {
      version: env.version,
    },

    refreshMinutes: 10,

    onLoad: handleBlockUpdate,
  });

  if (!block) return <>{children}</>;

  return (
    <Row
      alignItems="center"
      justifyContent="center"
      className="h-[100vh] bg-primary px-5 phone:px-2.5"
    >
      <Column
        gap="3"
        alignItems="center"
        className="w-full max-w-[max(25%,450px)] rounded-lg bg-primary-foreground p-8 text-center phone:p-6"
      >
        {block === 'MAINTENANCE' ? (
          <>
            {loading ? (
              <Loader type="dualRing" size={64} />
            ) : (
              <WarningIcon size={64} color="primary" />
            )}

            <h2 className="text-3xl">{t('maintenance-title')}</h2>

            <p>{t('maintenance-description')}</p>

            <Button onClick={() => retry()}>{t('retry')}</Button>
          </>
        ) : (
          <>
            <RefreshIcon size={64} color="primary" />

            <h2 className="text-3xl">{t('new-version-title')}</h2>

            <p>{t('new-version-description')}</p>

            <Button onClick={reload}>{t('refresh')}</Button>
          </>
        )}
      </Column>
    </Row>
  );
}
